import type { RichMarkdownTableCell, RichMarkdownTableData, RichMarkdownTableRow } from './types';

export const MARKDOWN_TABLE_SELECTOR = '[data-markdown-table]';

const decodeHtmlEntity = (entity: string): string => {
    if (entity.startsWith('&#x') || entity.startsWith('&#X')) {
        const value = Number.parseInt(entity.slice(3, -1), 16);
        return Number.isNaN(value) ? entity : String.fromCodePoint(value);
    }

    if (entity.startsWith('&#')) {
        const value = Number.parseInt(entity.slice(2, -1), 10);
        return Number.isNaN(value) ? entity : String.fromCodePoint(value);
    }

    const namedEntities: Record<string, string> = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&nbsp;': ' ',
    };

    return namedEntities[entity] ?? entity;
};

export const stripHtmlToPlainText = (html: string): string =>
    html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(?:p|div|li|tr|h[1-6])>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&(?:amp|lt|gt|quot|nbsp);|&#\d+;|&#x[0-9a-f]+;/gi, decodeHtmlEntity)
        .replace(/\n{3,}/g, '\n\n')
        .trim();

export const encodeMarkdownTablePayload = (value: unknown): string => encodeURIComponent(JSON.stringify(value));

export const decodeMarkdownTablePayload = <T = RichMarkdownTableData>(value: string | null | undefined): T | null => {
    if (!value) return null;

    try {
        return JSON.parse(decodeURIComponent(value)) as T;
    } catch {
        return null;
    }
};

export const preventSpreadsheetFormulaInjection = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

const escapeDelimitedCell = (value: string, delimiter: ',' | '\t'): string => {
    if (delimiter === '\t') {
        return value.replace(/\r?\n/g, ' ').replace(/\t/g, ' ');
    }

    if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
};

const toTextRows = (table: RichMarkdownTableData): string[][] => {
    const rows: string[][] = [];
    if (table.headers.length > 0) {
        rows.push(table.headers.map(cell => cell.text));
    }
    rows.push(...table.rows.map(row => row.map(cell => cell.text)));
    return rows;
};

export const tableToDelimitedText = (table: RichMarkdownTableData, delimiter: ',' | '\t'): string =>
    toTextRows(table)
        .map(row => row.map(cell => escapeDelimitedCell(delimiter === ',' ? preventSpreadsheetFormulaInjection(cell) : cell, delimiter)).join(delimiter))
        .join(delimiter === ',' ? '\r\n' : '\n');

export const tableToCsv = (table: RichMarkdownTableData): string => `\uFEFF${tableToDelimitedText(table, ',')}`;

export const tableToTsv = (table: RichMarkdownTableData): string => tableToDelimitedText(table, '\t');

export const createRichMarkdownTableCell = (html: string, align: RichMarkdownTableCell['align']): RichMarkdownTableCell => ({
    html,
    text: stripHtmlToPlainText(html),
    align: align ?? null,
});

export const normalizeRichMarkdownTableRows = (rows: RichMarkdownTableRow[]): RichMarkdownTableRow[] => {
    const width = Math.max(0, ...rows.map(row => row.length));
    return rows.map(row => {
        if (row.length >= width) return row;
        return [...row, ...Array.from({ length: width - row.length }, () => createRichMarkdownTableCell('', null))];
    });
};
